import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent7 as accent, useSlide7, Seg } from "./slide7.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 7 — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide7.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.65, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[]) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: "#c8f0ff", fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide7Mobile: React.FC = () => {
  const { meta: slide7Meta, waves: slide7Waves, callout: slide7Callout } = useSlide7();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#4a8ba0", textTransform: "uppercase", marginBottom: 8 }}>
        {slide7Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide7Meta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 27, lineHeight: 1.18, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide7Meta.titlePlain}<span style={{ color: accent }}>{slide7Meta.titleAccent}</span>
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#6a9ab0", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide7Meta.subtitle}
      </p>

      {/* five states */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide7Meta.statesLabel}</div>
        {slide7Waves.map((w, i) => (
          <div key={i} style={{
            display: "flex", gap: 13, alignItems: "center",
            background: w.htld ? "rgba(79,195,247,0.08)" : "rgba(255,255,255,0.02)",
            border: `1px solid ${w.htld ? "rgba(79,195,247,0.3)" : "rgba(79,195,247,0.1)"}`,
            borderRadius: 9, padding: "11px 13px", marginBottom: 8,
          }}>
            <div style={{ flexShrink: 0, width: 74 }}>
              <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 15, color: w.htld ? accent : "rgba(79,195,247,0.8)" }}>{w.name}</div>
              <div style={{ fontSize: 9, fontFamily: mono, color: "#4a6880", marginTop: 2 }}>{w.range}</div>
            </div>
            <p style={{ flex: 1, fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 13.5, lineHeight: 1.45, color: w.htld ? "#90dcf8" : "#9bbdd6", margin: 0 }}>{w.state}</p>
            {w.htld && (
              <div style={{ flexShrink: 0, fontSize: 9, fontFamily: mono, color: accent, background: "rgba(79,195,247,0.12)", padding: "3px 7px", borderRadius: 4 }}>
                {slide7Meta.badge}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* callout */}
      <div style={{ background: "rgba(79,195,247,0.06)", border: "1px solid rgba(79,195,247,0.20)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide7Meta.calloutLabel}</div>
        <p style={{ ...txt, color: "#9adcf8" }}>{renderSegs(slide7Callout)}</p>
      </div>
    </div>
  );
};

export default HTLDSlide7Mobile;
